/**
 * Dialog for editing the permission set of an ACTIVE sub-delegate.
 * Presentational — the caller owns the mutation (admin + owner flows use
 * different endpoints).
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { PermissionToggleGroup } from './PermissionToggleGroup';
import type { DelegationPermission } from './types';

type EditPermissionsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  delegateName: string;
  initialPermissions: DelegationPermission[];
  isSaving: boolean;
  errorMessage?: string | null;
  onSave: (permissions: DelegationPermission[]) => void;
};

export function EditPermissionsDialog({
  open,
  onOpenChange,
  delegateName,
  initialPermissions,
  isSaving,
  errorMessage = null,
  onSave,
}: EditPermissionsDialogProps) {
  const [permissions, setPermissions] =
    useState<DelegationPermission[]>(initialPermissions);

  useEffect(() => {
    if (open) setPermissions(initialPermissions);
    // Re-sync only when the dialog (re)opens for a row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-label={`Edit permissions for ${delegateName}`}>
        <DialogHeader>
          <DialogTitle>Edit permissions</DialogTitle>
          <DialogDescription>
            Choose what {delegateName} can do in this workspace. At least one
            permission is required.
          </DialogDescription>
        </DialogHeader>

        <PermissionToggleGroup
          value={permissions}
          onChange={setPermissions}
          disabled={isSaving}
        />

        {errorMessage && (
          <p className="text-xs text-red-600" role="alert">
            {errorMessage}
          </p>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(permissions)}
            disabled={isSaving || permissions.length === 0}
            className="rounded-md bg-[#026857] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#025246] disabled:opacity-50"
          >
            {isSaving ? 'Saving…' : 'Save permissions'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
